import React, { useState } from 'react';
import { AlertTriangleIcon, PlusIcon, EditIcon, TrashIcon, CheckCircleIcon } from 'lucide-react';
import { OrgUnitSelector } from '../orgunit/OrgUnitSelector';
import type { OrgUnit } from '../../financial/sharedFinancialData';
type PrecisionSetting = {
  id: string;
  scope: 'Global' | 'OrgUnit';
  orgUnitId?: string;
  orgUnitName?: string;
  orgUnitPath?: string;
  precision: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};
export function DecimalPrecisionConfiguration({
  locale = 'en-US'
}) {
  const [precisionSettings, setPrecisionSettings] = useState<PrecisionSetting[]>([{
    id: 'prec-001',
    scope: 'Global',
    precision: 2,
    isActive: true,
    createdAt: '2023-01-01T00:00:00Z',
    updatedAt: '2023-01-01T00:00:00Z'
  }, {
    id: 'prec-002',
    scope: 'OrgUnit',
    orgUnitId: 'ou-apac-sg',
    orgUnitName: 'Singapore Office',
    orgUnitPath: 'Global Technologies Inc. > APAC Region > Singapore Office',
    precision: 4,
    isActive: true,
    createdAt: '2023-03-15T00:00:00Z',
    updatedAt: '2023-03-15T00:00:00Z'
  }, {
    id: 'prec-003',
    scope: 'OrgUnit',
    orgUnitId: 'ou-apac-tokyo',
    orgUnitName: 'Tokyo Office',
    orgUnitPath: 'Global Technologies Inc. > APAC Region > Tokyo Office',
    precision: 2,
    isActive: true,
    createdAt: '2023-04-01T00:00:00Z',
    updatedAt: '2023-04-01T00:00:00Z'
  }]);
  const [showForm, setShowForm] = useState(false);
  const [editingSetting, setEditingSetting] = useState<PrecisionSetting | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  // Form state
  const [formScope, setFormScope] = useState<'Global' | 'OrgUnit'>('OrgUnit');
  const [formPrecision, setFormPrecision] = useState(2);
  const [formOrgUnitId, setFormOrgUnitId] = useState('');
  const [formOrgUnit, setFormOrgUnit] = useState<OrgUnit | null>(null);
  const handleAddNew = () => {
    setEditingSetting(null);
    setFormScope('OrgUnit');
    setFormPrecision(2);
    setFormOrgUnitId('');
    setFormOrgUnit(null);
    setShowForm(true);
  };
  const handleEdit = (setting: PrecisionSetting) => {
    setEditingSetting(setting);
    setFormScope(setting.scope);
    setFormPrecision(setting.precision);
    setFormOrgUnitId(setting.orgUnitId || '');
    setShowForm(true);
  };
  const handleDelete = (id: string) => {
    setDeletingId(id);
    setShowDeleteConfirm(true);
  };
  const confirmDelete = () => {
    if (deletingId) {
      setPrecisionSettings(precisionSettings.filter(s => s.id !== deletingId));
      setShowDeleteConfirm(false);
      setDeletingId(null);
    }
  };
  const handleSave = () => {
    if (editingSetting) {
      // Update existing
      setPrecisionSettings(precisionSettings.map(s => s.id === editingSetting.id ? {
        ...s,
        scope: formScope,
        orgUnitId: formScope === 'OrgUnit' ? formOrgUnitId : undefined,
        orgUnitName: formScope === 'OrgUnit' ? formOrgUnit?.name : undefined,
        orgUnitPath: formScope === 'OrgUnit' ? formOrgUnit?.path : undefined,
        precision: formPrecision,
        updatedAt: new Date().toISOString()
      } : s));
    } else {
      // Create new
      const newSetting: PrecisionSetting = {
        id: `prec-${Date.now()}`,
        scope: formScope,
        orgUnitId: formScope === 'OrgUnit' ? formOrgUnitId : undefined,
        orgUnitName: formScope === 'OrgUnit' ? formOrgUnit?.name : undefined,
        orgUnitPath: formScope === 'OrgUnit' ? formOrgUnit?.path : undefined,
        precision: formPrecision,
        isActive: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      setPrecisionSettings([...precisionSettings, newSetting]);
    }
    setShowForm(false);
    setEditingSetting(null);
  };
  const handleCancel = () => {
    setShowForm(false);
    setEditingSetting(null);
  };
  const handleOrgUnitChange = (orgUnitId: string, orgUnit: OrgUnit | null) => {
    setFormOrgUnitId(orgUnitId);
    setFormOrgUnit(orgUnit);
  };
  const globalSetting = precisionSettings.find(s => s.scope === 'Global');
  const orgUnitSettings = precisionSettings.filter(s => s.scope === 'OrgUnit');
  return <div data-id="element-17146">
      <div className="mb-8" data-id="element-17147">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-17148">
          Decimal Precision Configuration
        </h2>
        <p className="mt-1 text-sm text-gray-500" data-id="element-17149">
          Configure decimal places for currency fields at global and
          organisation unit levels
        </p>
      </div>

      {/* Warning Banner */}
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6" data-id="element-17150">
        <div className="flex" data-id="element-17151">
          <div className="flex-shrink-0" data-id="element-17152">
            <AlertTriangleIcon className="h-5 w-5 text-yellow-400" data-id="element-17153" />
          </div>
          <div className="ml-3" data-id="element-17154">
            <p className="text-sm text-yellow-700" data-id="element-17155">
              ⚠️ Changing precision settings will affect currency fields across
              RapidP2P. OrgUnit-specific settings override the global default.
              Changes should only be made by authorised financial
              administrators.
            </p>
          </div>
        </div>
      </div>

      {!showForm ? <>
          {/* Global Default Setting */}
          <div className="bg-white shadow rounded-lg mb-6" data-id="element-17156">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-17157">
              <div className="flex items-center justify-between" data-id="element-17158">
                <div data-id="element-17159">
                  <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-17160">
                    Global Default
                  </h3>
                  <p className="mt-1 text-sm text-gray-500" data-id="element-17161">
                    Default precision applied to all organisation units unless
                    overridden
                  </p>
                </div>
                {globalSetting && <button onClick={() => handleEdit(globalSetting)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-17162">
                    <EditIcon className="h-4 w-4 mr-2" data-id="element-17163" />
                    Edit
                  </button>}
              </div>
            </div>
            {globalSetting && <div className="px-4 py-5 sm:px-6" data-id="element-17164">
                <div className="flex items-center justify-between" data-id="element-17165">
                  <div data-id="element-17166">
                    <span className="text-sm font-medium text-gray-500" data-id="element-17167">
                      Decimal Places:
                    </span>
                    <span className="ml-2 text-2xl font-bold text-gray-900" data-id="element-17168">
                      {globalSetting.precision}
                    </span>
                    <span className="ml-2 text-sm text-gray-500" data-id="element-17169">
                      (
                      {globalSetting.precision === 2 ? 'Standard' : 'High Precision'}
                      )
                    </span>
                  </div>
                  <CheckCircleIcon className="h-6 w-6 text-green-500" data-id="element-17170" />
                </div>
              </div>}
          </div>

          {/* OrgUnit Overrides */}
          <div className="bg-white shadow rounded-lg" data-id="element-17171">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-17172">
              <div className="flex items-center justify-between" data-id="element-17173">
                <div data-id="element-17174">
                  <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-17175">
                    Organisation Unit Overrides
                  </h3>
                  <p className="mt-1 text-sm text-gray-500" data-id="element-17176">
                    Specific precision settings for individual organisation
                    units
                  </p>
                </div>
                <button onClick={handleAddNew} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-17177">
                  <PlusIcon className="h-4 w-4 mr-2" data-id="element-17178" />
                  Add Override
                </button>
              </div>
            </div>

            {orgUnitSettings.length === 0 ? <div className="px-4 py-12 text-center" data-id="element-17179">
                <p className="text-sm text-gray-500" data-id="element-17180">
                  No organisation unit overrides configured. All organisation
                  units use the global default.
                </p>
              </div> : <div className="overflow-x-auto" data-id="element-17181">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-17182">
                  <thead className="bg-gray-50" data-id="element-17183">
                    <tr data-id="element-17184">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-17185">
                        Organisation Unit
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-17186">
                        Decimal Places
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-17187">
                        Status
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-17188">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-17189">
                    {orgUnitSettings.map(setting => <tr key={setting.id} data-id="element-17190">
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-17191">
                          <div className="text-sm font-medium text-gray-900" data-id="element-17192">
                            {setting.orgUnitName}
                          </div>
                          <div className="text-sm text-gray-500" data-id="element-17193">
                            {setting.orgUnitPath}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-17194">
                          <div className="text-sm font-bold text-gray-900" data-id="element-17195">
                            {setting.precision} decimal places
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-17196">
                            {setting.precision === 2 ? 'Standard' : 'High Precision'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-17197">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-17198">
                            <CheckCircleIcon className="h-3 w-3 mr-1" data-id="element-17199" />
                            Active
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-17200">
                          <button onClick={() => handleEdit(setting)} className="text-indigo-600 hover:text-indigo-900 mr-4" data-id="element-17201">
                            <EditIcon className="h-4 w-4 inline" data-id="element-17202" />
                          </button>
                          <button onClick={() => handleDelete(setting.id)} className="text-red-600 hover:text-red-900" data-id="element-17203">
                            <TrashIcon className="h-4 w-4 inline" data-id="element-17204" />
                          </button>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>}
          </div>
        </> : <div className="bg-white shadow rounded-lg" data-id="element-17205">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-17206">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-17207">
              {editingSetting ? 'Edit Precision Setting' : 'Add Precision Override'}
            </h3>
          </div>
          <div className="px-4 py-5 sm:p-6 space-y-6" data-id="element-17208">
            {/* Scope */}
            <div data-id="element-17209">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-17210">
                Scope
              </label>
              <select value={formScope} onChange={e => setFormScope(e.target.value as 'Global' | 'OrgUnit')} disabled={editingSetting?.scope === 'Global'} className="block w-full max-w-xs shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md disabled:bg-gray-100 disabled:cursor-not-allowed" data-id="element-17211">
                <option value="Global" data-id="element-17212">Global (Default)</option>
                <option value="OrgUnit" data-id="element-17213">Organisation Unit</option>
              </select>
              {editingSetting?.scope === 'Global' && <p className="mt-1 text-xs text-gray-500" data-id="element-17214">
                  Global scope cannot be changed
                </p>}
            </div>

            {/* OrgUnit Selector */}
            {formScope === 'OrgUnit' && <div data-id="element-17215">
                <OrgUnitSelector value={formOrgUnitId} onChange={handleOrgUnitChange} label="Organisation Unit" placeholder="Select organisation unit" required={true} showPath={true} data-id="element-17216" />
              </div>}

            {/* Precision */}
            <div data-id="element-17217">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-17218">
                Decimal Precision
              </label>
              <select value={formPrecision} onChange={e => setFormPrecision(parseInt(e.target.value))} className="block w-full max-w-xs shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md" data-id="element-17219">
                <option value={2} data-id="element-17220">2 Decimal Places (Standard)</option>
                <option value={4} data-id="element-17221">4 Decimal Places (High Precision)</option>
              </select>
              <p className="mt-1 text-sm text-gray-500" data-id="element-17222">
                Defines how many decimal places are stored and displayed for
                currency values
              </p>
            </div>
          </div>

          <div className="px-4 py-3 bg-gray-50 text-right sm:px-6 space-x-3" data-id="element-17223">
            <button onClick={handleCancel} className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-17224">
              Cancel
            </button>
            <button onClick={handleSave} disabled={formScope === 'OrgUnit' && !formOrgUnitId} className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-300 disabled:cursor-not-allowed" data-id="element-17225">
              {editingSetting ? 'Update' : 'Create'}
            </button>
          </div>
        </div>}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && <div className="fixed z-10 inset-0 overflow-y-auto" data-id="element-17226">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-17227">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true" data-id="element-17228">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-17229"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-17230">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-17231">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-17232">
                <div className="sm:flex sm:items-start" data-id="element-17233">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10" data-id="element-17234">
                    <AlertTriangleIcon className="h-6 w-6 text-red-600" data-id="element-17235" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left" data-id="element-17236">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-17237">
                      Delete Precision Override
                    </h3>
                    <div className="mt-2" data-id="element-17238">
                      <p className="text-sm text-gray-500" data-id="element-17239">
                        Are you sure you want to delete this precision override?
                        The OrgUnit will revert to using the tenant default
                        precision.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-17240">
                <button type="button" onClick={confirmDelete} className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-17241">
                  Delete
                </button>
                <button type="button" onClick={() => setShowDeleteConfirm(false)} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-17242">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}